import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import useStore from '../../store/useStore';
import { orchard } from '../../theme';
import MasteryRing from '../fx/MasteryRing';
import { getProgress, onProgress, setLearnFlags } from './learnApi';
import { BY_KEY, LESSONS } from './lessons';

/* Dashboards: «Обучение» — how far along, the next lesson, hide. */
export default function LearnCard() {
  const navigate = useNavigate();
  const { language } = useStore();
  const en = language === 'en';
  const [p, setP] = useState(null);
  useEffect(() => {
    let stop = false;
    getProgress()
      .then((x) => !stop && setP(x))
      .catch(() => {});
    const off = onProgress((x) => setP(x));
    return () => {
      stop = true;
      off();
    };
  }, []);
  if (!p || p.cardHiddenAt || p.done >= p.total) return null;
  const doneKeys = new Set(p.lessons.filter((l) => l.done).map((l) => l.key));
  const next = LESSONS.find((l) => !doneKeys.has(l.key));
  const pct = Math.round((p.done / p.total) * 100);
  return (
    <Box
      data-learn-card
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: { xs: 1.75, sm: 2.25 },
        mb: 2.5,
        borderRadius: '22px',
        border: '1px solid',
        borderColor: alpha(orchard.honey, 0.45),
        background: (t) =>
          `linear-gradient(120deg, ${alpha(orchard.honey, t.palette.mode === 'dark' ? 0.16 : 0.14)}, ${alpha(orchard.cherry, t.palette.mode === 'dark' ? 0.14 : 0.06)})`,
      }}
    >
      <Box sx={{ color: orchard.cherry, flexShrink: 0 }}>
        <MasteryRing value={pct} color={orchard.cherry} size={62} stroke={6} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            fontSize: '0.64rem',
          }}
        >
          <SchoolOutlinedIcon sx={{ fontSize: 15 }} />
          {en ? 'Learn Core' : 'Обучение'} · {p.done}/{p.total}
        </Typography>
        <Typography sx={{ fontWeight: 800, lineHeight: 1.3 }}>
          {p.done === 0
            ? en
              ? 'What Core can do — short lessons with hints on the screen'
              : 'Что умеет Core — короткие уроки с подсказками на экране'
            : en
              ? `Next: ${BY_KEY[next.key].title.en}`
              : `Дальше: ${BY_KEY[next.key].title.ru}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => navigate(`/learn?open=${next.key}`)}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            {p.done === 0
              ? en
                ? 'Start'
                : 'Начать'
              : en
                ? 'Continue'
                : 'Продолжить'}
          </Button>
          <Button
            size="small"
            onClick={() => navigate('/learn')}
            sx={{ textTransform: 'none' }}
          >
            {en ? 'All lessons' : 'Все уроки'}
          </Button>
        </Box>
      </Box>
      <Tooltip
        title={en ? 'Hide (it stays in the menu)' : 'Скрыть (останется в меню)'}
      >
        <IconButton
          size="small"
          aria-label={en ? 'Hide' : 'Скрыть'}
          onClick={() => setLearnFlags({ cardHidden: true }).catch(() => {})}
          sx={{ alignSelf: 'flex-start' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
